import { Body, Button, Heading, IconButton } from '@assembly-js/design-system';
import { Dialog } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Magic links give a client access to a portal without a password.
 * This dialog allows internal users to generate a magic link for a client.
 */

import { Callout } from 'src/components/Callout';
import { RowDivider } from 'src/components/RowDivider';
import { Client } from 'src/constants';
import { StudioChecklistTaskStatus } from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import { useCopyToClipboard } from 'src/hooks/useCopyToClipboard';
import { useIsStudioUser } from 'src/hooks/useIsStudioUser';
import { useAppDispatch } from 'src/hooks/useStore';
import { TaskCodes } from 'src/legacy/components/HomePage/OnboardingTasksSection/constants';
import { useUpdateStudioTaskStatusMutation } from 'src/services/api/improvedOnboardingApi';
import {
  CreateMagicLinkRequest,
  useCreateMagicLinkMutation,
} from 'src/services/api/magicLinksApi';
import { alertSnackbar } from 'src/store/ui/actions';
import { isSafari } from 'src/utils/DeviceUtils';

const ENTITY_TYPE_LABEL = {
  CLIENT_USER: 'magic',
  INVOICE: 'payment',
  CONTRACT: 'eSign',
  FORM_TEMPLATE: 'form',
  FOLDER: 'folder',
} as const;

const ENTITY_TYPE_SECURITY_DESC = {
  CLIENT_USER: '',
  INVOICE: 'pay this invoice',
  CONTRACT: 'sign this contract',
  FORM_TEMPLATE: 'fill out this form',
  FOLDER: 'access this folder',
};
export type ShareTypes = { email: boolean; link: boolean };

export function CreateClientMagicLinkDialog({
  clients,
  entityType = 'CLIENT_USER',
  nextPath = '/',
  onCancel,
  onSuccess,
  open,
  shareTypes,
  showOnboardingCallout = false,
}: {
  clients: Client[];
  entityType?: CreateMagicLinkRequest['entityType'];
  nextPath?: CreateMagicLinkRequest['nextPath'];
  onCancel: () => void;
  onSuccess: () => void;
  open: boolean;
  shareTypes: ShareTypes;
  showOnboardingCallout?: boolean;
}) {
  const { t } = useTranslation();
  // This state is used to manage a workaround for Safari's
  // async clipboard API restrictions. They explicitly ban clipboard manipulation
  // after an await call, so we needed to tweak the UX to create an explicit user
  // interaction right before the copy without waiting on the network call.
  const [safariWorkaroundMagicLink, setSafariWorkaroundMagicLink] =
    React.useState<string | null>(null);

  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
  const [createMagicLink] = useCreateMagicLinkMutation();
  const dispatch = useAppDispatch();
  const copyToClipboard = useCopyToClipboard();

  // Copying a client's magic link is a second way to start the Studio "explore the client
  // experience" task; it only moves the task to in-progress. Completion is handled server-side
  // when the client genuinely activates (see completeStudioFirstClientTask).
  const isStudioUser = useIsStudioUser();
  const isClientMagicLink = entityType === 'CLIENT_USER';
  const [updateStudioTaskStatus] = useUpdateStudioTaskStatusMutation();
  // Latch so rapid re-copies (incl. the Safari two-step) only fire the update once per dialog.
  const hasMarkedInProgressRef = React.useRef(false);

  const markExploreClientInProgress = () => {
    if (!isStudioUser || !isClientMagicLink || hasMarkedInProgressRef.current) {
      return;
    }
    // Fire optimistically without waiting on the onboarding query: the task only advances
    // todo → in-progress here and the backend enforces forward-only transitions, so this safely
    // no-ops if it is already in-progress or done (done is owned by the client-activation path).
    // Not gating on the fetched status avoids dropping the update on a fast copy before it loads.
    hasMarkedInProgressRef.current = true;
    void updateStudioTaskStatus({
      taskCode: TaskCodes.TaskCodeFirstClient,
      status: StudioChecklistTaskStatus.InProgress,
    });
  };
  const entityCopy = ENTITY_TYPE_SECURITY_DESC[entityType];
  const singleClient = clients.length === 1 ? clients.at(0) : null;
  const label = ENTITY_TYPE_LABEL[entityType];
  const shareCopy =
    shareTypes.email && shareTypes.link
      ? 'Share'
      : shareTypes.email
        ? 'Email'
        : 'Copy';
  const clientsString = clients
    .map((client, idx) => {
      let prefix = '';
      if (clients.length > 2 && idx !== 0) {
        prefix = prefix + ', ';
      }
      if (clients.length > 1 && idx === clients.length - 1) {
        prefix = prefix + `${clients.length === 2 ? ' ' : ''}and `;
      }
      return `${prefix}${client.fields.givenName} ${client.fields.familyName} (${client.fields.email})`;
    })
    .join('');

  const emailContentsDescription =
    clients.length > 1 ? 'single-use magic links' : 'a single-use magic link';

  const handleCopyMagicLink = async (link: string) => {
    const copied = await copyToClipboard(link, {
      successMessage: 'Link copied to clipboard',
    });
    if (!copied) return;
    markExploreClientInProgress();
    onSuccess();
  };

  const handleCreateLink = async (type: 'link' | 'email', skipCopy = false) => {
    if (singleClient) {
      try {
        setIsButtonDisabled(true);
        const response = await createMagicLink({
          entityType,
          nextPath,
          type,
          userId: singleClient.id,
        }).unwrap();
        setIsButtonDisabled(false);
        if (response.linkUrl) {
          // To support the safari workaround.
          if (skipCopy) {
            setSafariWorkaroundMagicLink(response.linkUrl);
          } else {
            await handleCopyMagicLink(response.linkUrl);
          }
        } else {
          dispatch(
            alertSnackbar({
              successMessage: t(($) => $.messages.link_sent_to, {
                email: singleClient.fields.email,
              }),
            }),
          );
          onSuccess();
        }
      } catch (error) {
        dispatch(
          alertSnackbar({
            errorMessage: 'Failed to create magic link, please try again',
          }),
        );
        console.error('Failed to create magic link:', error);
      } finally {
        setIsButtonDisabled(false);
      }
    } else {
      // when multiple clients are created
      // for each client send a magic link email
      try {
        await Promise.all(
          clients.map((client) =>
            createMagicLink({
              entityType,
              nextPath,
              type,
              userId: client.id,
            }),
          ),
        );

        dispatch(
          alertSnackbar({
            successMessage: t(($) => $.messages.emails_sent_to_clients, {
              count: clients.length,
            }),
          }),
        );
        onSuccess();
      } catch (error) {
        dispatch(
          alertSnackbar({
            errorMessage: 'Failed to create magic link, please try again',
          }),
        );
        console.error('Failed to create magic link:', error);
      }
    }
  };

  return (
    <Dialog classes={{ paper: 'max-w-[470px]' }} onClose={onCancel} open={open}>
      <div className="max-w-[470px]">
        <header className="px-5 py-4 border-0 border-b border-gray-100 border-solid flex justify-between items-center">
          <Heading size="lg">
            {shareCopy} {label} link{clients.length > 1 ? 's' : ''}
          </Heading>
          <IconButton icon="Close" onClick={onCancel} variant="minimal" />
        </header>
        <main className="px-5 py-4">
          {showOnboardingCallout && (
            <Callout
              status="info"
              label={t(($) => $.messages.try_client_experience_hint)}
              className="mb-2.5"
            />
          )}

          {shareTypes.email && (
            <>
              <Button
                data-testid="email-access-link"
                className={['w-full', 'mb-2'].join(' ')}
                label={`Email magic link${clients.length > 1 ? 's' : ''}`}
                onClick={async () => {
                  await handleCreateLink('email');
                }}
                variant="secondary"
              />

              <>
                {!entityCopy && !shareTypes.link && (
                  <Body size="base">
                    <span className="font-medium">{`${clientsString}`}</span>
                    {` will receive ${emailContentsDescription} that provides full access to their account${
                      clients.length > 1 ? 's' : ''
                    }.`}
                  </Body>
                )}
                {entityCopy && !shareTypes.link && (
                  <Body size="base">
                    <span className="font-medium">{`${clientsString}`}</span>
                    {` will receive ${emailContentsDescription} that let${
                      clients.length > 1 ? '' : 's'
                    } them ${entityCopy} and provides full access to their account`}
                  </Body>
                )}
              </>
            </>
          )}

          {shareTypes.email && shareTypes.link && (
            <RowDivider className="my-5">
              <Body size="xs">OR</Body>
            </RowDivider>
          )}

          {shareTypes.link && singleClient && (
            <>
              <Button
                className={['w-full', 'mb-2'].join(' ')}
                disabled={isButtonDisabled}
                label={
                  isSafari() && !safariWorkaroundMagicLink
                    ? `Create ${label} link`
                    : `Copy ${label} link`
                }
                onClick={async () => {
                  if (isSafari() && safariWorkaroundMagicLink) {
                    handleCopyMagicLink(safariWorkaroundMagicLink);
                  } else if (isSafari() && !safariWorkaroundMagicLink) {
                    await handleCreateLink('link', isSafari());
                  } else {
                    await handleCreateLink('link');
                  }
                }}
                variant="secondary"
              />
              {/* Only show the copy magic link option when only one client is created since
                /* we don't support multi magic link copy to clipboard */}
              {
                <>
                  {!entityCopy && !shareTypes.email && (
                    <Body size="base">
                      {`This single-use magic link gives the recipient full access to `}
                      <span className="font-medium">
                        {`${singleClient.fields.givenName} ${singleClient.fields.familyName}'s (${singleClient.fields.email}) `}
                      </span>
                      {`account. Treat it like a password and share it only with the intended recipient.`}
                    </Body>
                  )}
                  {entityCopy && !shareTypes.email && (
                    <Body size="base">
                      {`This single-use magic link lets the recipient ${entityCopy} and provides full access to `}
                      <span className="font-medium">
                        {`${singleClient.fields.givenName} ${singleClient.fields.familyName}'s (${singleClient.fields.email}) `}
                      </span>
                      {`account. Treat it like a password and share it only with the intended recipient.`}
                    </Body>
                  )}
                </>
              }
            </>
          )}
          {singleClient && shareTypes.email && shareTypes.link && (
            <Body size="base">
              {`A single use magic link provides full access to `}
              <span className="font-medium">
                {`${singleClient.fields.givenName} ${singleClient.fields.familyName}'s (${singleClient.fields.email}) `}
              </span>
              {`account. Treat it like a password and share it only with the intended recipient.`}
            </Body>
          )}
        </main>
        <footer className="px-5 py-4 flex justify-end border-0 border-t border-gray-100 border-solid">
          <Button label="Close" onClick={onCancel} variant="secondary" />
        </footer>
      </div>
    </Dialog>
  );
}
